import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { DEFAULT_RADIOS } from '../data/defaultRadios';
import { readJSON, mergeStations } from '../utils/storage';
import { DEFAULT_PLACEHOLDER_IMAGE, ISLAMIC_IMAGES_GALLERY } from '../data/islamicImages';
import { THEMES, DEFAULT_THEME_ID, findTheme, applyTheme, THEME_BY_PRAYER, getAutoThemeId, buildThemeSchedule } from '../data/themes';
import { syncStatusBar, saveNativeThemeSchedule } from '../utils/nativeSetup';
import {
  hasNativePlayer,
  nativePlay,
  nativePause,
  nativeResume,
  nativeStop,
  nativeSetVolume,
  nativeSetStations,
  nativeGetState,
  onNativeState,
} from '../utils/nativePlayer';
import { autoDetect, readStoredLocation, clearStoredLocation, detectLocation } from '../utils/location';
import { DEFAULT_CITY_ID, findCity } from '../data/cities';
import { DEFAULT_METHOD_ID, getCurrentPrayer, getPrayerTimes } from '../utils/prayerTimes';
import { useAdhanNotifications } from '../hooks/useAdhanNotifications';
import { normalizeAthkarDelay } from '../utils/notificationPlan';
import {
  fetchCurrentUser,
  isApiConfigured,
  fetchAppSettings,
  updateAppSettings,
  fetchStations,
  publishStations,
  fetchGallery,
  addGalleryImage,
  deleteGalleryImage,
  PERMISSIONS,
} from '../api/client';

const AudioContext = createContext(null);

// مهل إعادة المحاولة للرابط نفسه قبل الحكم بتعطّله (~٩ ثوانٍ في مجموعها)
const RETRY_DELAYS_MS = [500, 1000, 1500, 2500, 3500];

export const AudioProvider = ({ children }) => {
  // Audio element reference
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  // يميّز محاولة التشغيل الحالية لإلغاء نتائج المحاولات القديمة
  const playTokenRef = useRef(0);

  // حارس التعثّر.
  //
  // حدث waiting يُشعل مؤشّر التحميل، ولا شيء يطفئه إن لم يصل البث إلى
  // playing ولم يُطلق خطأً — وهذا يحدث مع خادم يقبل الاتصال ثم يصمت.
  // كانت النتيجة مؤشّراً يدور إلى الأبد بلا رسالة ولا مخرج.
  const stallTimerRef = useRef(null);

  // داخل التطبيق يُشغّل أندرويد نفسه (RadioService) لا عنصر <audio>.
  // يُقرأ مرة: المنصّة لا تتبدّل أثناء الجلسة.
  const nativeEngine = useRef(hasNativePlayer()).current;

  // Audio Playback State
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('moslam_volume');
    return saved !== null ? parseFloat(saved) : 0.85;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // جلسة المستخدم — مصدرها الخادم وحده.
  //
  // كانت لوحة التحكم خلف رمز يُحفظ على الجهاز. الرمز لم يكن يحمي شيئاً:
  // صلاحية التعديل يتحقق منها الخادم من الجلسة على كل طلب، فكان الرمز
  // بوابة أمام المالك وحده. الآن صفة «مسؤول» في الحساب هي المفتاح.
  const [currentUser, setCurrentUser] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(isApiConfigured());

  const refreshSession = async () => {
    if (!isApiConfigured()) return null;
    const user = await fetchCurrentUser();
    setCurrentUser(user);
    setIsSessionLoading(false);
    return user;
  };

  useEffect(() => {
    let cancelled = false;
    if (!isApiConfigured()) return;
    fetchCurrentUser().then((user) => {
      if (cancelled) return;
      setCurrentUser(user);
      setIsSessionLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // تنظيف مفاتيح النسخ القديمة غير الآمنة
  useEffect(() => {
    localStorage.removeItem('moslam_admin_logged_in');
    localStorage.removeItem('moslam_admin_pin');
  }, []);

  const isAdminLoggedIn = currentUser?.role === 'admin';

  // صلاحيات البث. المسؤول يملكها كلها ضمناً، والمذيع ما مُنح منها.
  // مصدرها الجلسة من الخادم — والخادم يتحقّق منها مستقلاً عند كل طلب،
  // فما هنا للعرض لا للحماية.
  const userPermissions = Array.isArray(currentUser?.permissions)
    ? currentUser.permissions
    : [];
  const hasPermission = (key) => isAdminLoggedIn || userPermissions.includes(key);
  const canAnswerCalls = hasPermission(PERMISSIONS.ANSWER_CALLS);
  const canOpenRoom = hasPermission(PERMISSIONS.OPEN_ROOM);
  const canManageRoom = hasPermission(PERMISSIONS.MANAGE_ROOM);
  // المذيع: من يملك صلاحية ولا يملك اللوحة كاملة
  const isBroadcaster = !isAdminLoggedIn && userPermissions.length > 0;
  const isStaff = isAdminLoggedIn || isBroadcaster;

  // الثيم.
  //
  // 'auto' يتبع وقت الصلاة الحالي: التطبيق يشرق مع الضحى ويهدأ مع العشاء
  // دون تدخّل. هذا سبب وجود نظام الثيمات أصلاً — اللون يخبرك أين أنت
  // من اليوم، لا مجرد تفضيل جمالي.
  // اختيار المستخدم يُحفظ على جهازه ويسبق كل شيء. ومن لم يختر يتبع
  // ثيمَ المسؤول من الخادم. كان الثيم محلياً بحتاً، فكان المسؤول
  // يغيّره فلا يتغيّر عند أحد.
  const [themeChoice, setThemeChoice] = useState(
    () => localStorage.getItem('moslam_theme') || 'auto'
  );
  const [userPickedTheme] = useState(() => localStorage.getItem('moslam_theme') !== null);
  // يضبطه قسم المواقيت عند تغيّر الوقت الحالي (محسوب فورياً لمنع وميض الثيم)
  const [prayerPeriod, setPrayerPeriod] = useState(() => {
    try {
      const storedGps = readStoredLocation();
      const storedCity = localStorage.getItem('moslam_selected_city') || DEFAULT_CITY_ID;
      const loc = storedGps ?? findCity(storedCity) ?? findCity(DEFAULT_CITY_ID);
      if (!loc) return null;
      const methodId = localStorage.getItem('moslam_calc_method') || loc.method || DEFAULT_METHOD_ID;
      const madhab = localStorage.getItem('moslam_madhab') || 'shafi';
      const current = getCurrentPrayer({
        lat: loc.lat,
        lng: loc.lng,
        now: new Date(),
        methodId,
        madhab,
        timeZone: loc.tz,
      });
      return current?.key ?? null;
    } catch {
      return null;
    }
  });

  /* ------------------------------- الموقع ------------------------------- */
  //
  // مصدر واحد للموقع. سابقاً كان محلياً داخل شاشة المواقيت، فبعد تحديده
  // بالـ GPS تعرض تلك الشاشة مدينتك بينما يبقى خط اليوم في الرئيسية على
  // مكة — شاشتان تقولان وقتين مختلفين لنفس اللحظة.

  const [gpsLocation, setGpsLocation] = useState(() => readStoredLocation());
  const [selectedCityId, setSelectedCityId] = useState(
    () => localStorage.getItem('moslam_selected_city') || DEFAULT_CITY_ID
  );
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState('');

  // الموقع الفعّال: الـ GPS يسبق المدينة المختارة
  const location = gpsLocation ?? findCity(selectedCityId) ?? findCity(DEFAULT_CITY_ID);

  // تحديد تلقائي عند الإقلاع — بلا نقر.
  // autoDetect لا يطلب الإذن إلا مرة واحدة، ولا يطلبه إطلاقاً لمن رفضه.
  useEffect(() => {
    let cancelled = false;
    autoDetect().then((found) => {
      if (!cancelled && found) setGpsLocation(found);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  /** تحديد يدوي بضغط زر "موقعي" — يطلب الإذن صراحةً ويبلّغ بالنتيجة. */
  const locateMe = async () => {
    setIsLocating(true);
    setLocationError('');
    try {
      const found = await detectLocation({ highAccuracy: true });
      setGpsLocation(found);
      return found;
    } catch (err) {
      setLocationError(err.message);
      return null;
    } finally {
      setIsLocating(false);
    }
  };

  /** العودة إلى اختيار مدينة من القائمة. */
  const selectCity = (cityId) => {
    setGpsLocation(null);
    clearStoredLocation();
    setSelectedCityId(cityId);
    localStorage.setItem('moslam_selected_city', cityId);
  };

  /* ------------------------ حساب المواقيت والتنبيهات ------------------------ */

  // طريقة الحساب والمذهب كانا في قسم المواقيت وحده، فكانت جدولة الأذان
  // حبيسة ذلك التبويب. صارا هنا ليقرأهما القسمُ والجدولةُ معاً.
  const [calcMethodId, setCalcMethodIdState] = useState(
    () => localStorage.getItem('moslam_calc_method') || ''
  );
  const [madhab, setMadhabState] = useState(
    () => localStorage.getItem('moslam_madhab') || 'shafi'
  );

  // اختيار المؤذّن — تفضيل جهاز لا إعداد موقع: لكلٍّ ما يألف صوته.
  // فارغ يعني الأذان المضمَّن في التطبيق.
  const [adhanUrl, setAdhanUrlState] = useState(
    () => localStorage.getItem('moslam_adhan_url') || ''
  );

  const setAdhanUrl = (url) => {
    setAdhanUrlState(url || '');
    if (url) localStorage.setItem('moslam_adhan_url', url);
    else localStorage.removeItem('moslam_adhan_url');
  };

  const setCalcMethodId = (id) => {
    setCalcMethodIdState(id);
    if (id) localStorage.setItem('moslam_calc_method', id);
    else localStorage.removeItem('moslam_calc_method');
  };

  const setMadhab = (value) => {
    setMadhabState(value);
    localStorage.setItem('moslam_madhab', value);
  };

  // تذكير الأذكار بعد الصلاة: مفعّل ما لم يُطفأ صراحة
  const [athkarNotify, setAthkarNotifyState] = useState(
    () => localStorage.getItem('moslam_athkar_notify') !== '0'
  );
  const [athkarDelay, setAthkarDelayState] = useState(
    () => normalizeAthkarDelay(localStorage.getItem('moslam_athkar_delay'))
  );

  const setAthkarNotify = (on) => {
    setAthkarNotifyState(on);
    localStorage.setItem('moslam_athkar_notify', on ? '1' : '0');
  };

  const setAthkarDelay = (minutes) => {
    setAthkarDelayState(minutes);
    localStorage.setItem('moslam_athkar_delay', String(minutes));
  };

  // اختيار المستخدم يسبق الطريقة المعتمدة في بلد المدينة
  const effectiveMethod = calcMethodId || location.method || DEFAULT_METHOD_ID;

  // تنبيهات الأذان: لا مفتاح لها ولا تفضيل محفوظ — تعمل دائماً.
  // تُركَّب هنا مرة واحدة فتبقى حيّة أياً كان التبويب المفتوح.
  const adhan = useAdhanNotifications({
    lat: location.lat,
    lng: location.lng,
    methodId: effectiveMethod,
    madhab,
    timeZone: location.tz,
    cityName: location.name,
    adhanUrl,
    athkar: athkarNotify,
    athkarDelayMin: athkarDelay,
  });

  /** ينشر قائمة الإذاعات كما هي عند المسؤول فتصل كل جهاز. */
  const publishStationList = async () => {
    await publishStations(
      stations.map((st) => ({
        id: st.id,
        name: st.name,
        reciter: st.reciter,
        country: st.country,
        countryCode: st.countryCode,
        countryFlag: st.countryFlag,
        url: st.url,
        category: st.category,
        description: st.description,
        image: st.image,
        isCustom: Boolean(st.isCustom),
        isEnabled: st.isEnabled !== false,
      }))
    );
  };

  /** يدفع إعدادات الموقع إلى الخادم فتصل كل جهاز. للمسؤول وحده. */
  const publishSiteSettings = async (extra = {}) => {
    const payload = {
      siteTitle: siteSettings.siteTitle ?? '',
      siteSubtitle: siteSettings.siteSubtitle ?? '',
      announcementText: siteSettings.announcementText ?? '',
      showAnnouncement: Boolean(siteSettings.showAnnouncement),
      showHeroStreamInput: Boolean(siteSettings.showHeroStreamInput),
      autoPlayOnLoad: Boolean(siteSettings.autoPlayOnLoad),
      defaultStationId: siteSettings.defaultStationId ?? '',
      studioLocked: Boolean(siteSettings.studioLocked),
      majlisOpen: Boolean(siteSettings.majlisOpen),
      studioWhatsapp: siteSettings.studioWhatsapp ?? '',
      studioPhone: siteSettings.studioPhone ?? '',
      studioEmail: siteSettings.studioEmail ?? '',
      // الغياب ظهور: إعداداتٌ من نسخة سابقة لا تُخفي شيئاً
      showMushaf: siteSettings.showMushaf !== false,
      showMajlis: siteSettings.showMajlis !== false,
      showFatwa: siteSettings.showFatwa !== false,
      showStudio: siteSettings.showStudio !== false,
      ...extra,
    };
    await updateAppSettings(payload);
  };

  const activeThemeId =
    themeChoice === 'auto'
      ? getAutoThemeId(prayerPeriod)
      : themeChoice;

  const activeTheme = findTheme(activeThemeId);

  const changeTheme = (id) => {
    setThemeChoice(id);
    localStorage.setItem('moslam_theme', id);
    const th = findTheme(id === 'auto' ? getAutoThemeId(prayerPeriod) : id);
    applyTheme(th);
    syncStatusBar(th);
  };

  // تطبيق الرموز على الجذر فوراً قبل الرسم — يمنع أي وميض للثيم
  useEffect(() => {
    applyTheme(activeTheme);
    syncStatusBar(activeTheme);
  }, [activeTheme]);

  // جدول الثيم التلقائي للإقلاع القادم — انظر buildThemeSchedule
  useEffect(() => {
    let schedule = [];
    try {
      const day = 24 * 60 * 60 * 1000;
      const now = Date.now();
      schedule = buildThemeSchedule(
        [-1, 0, 1].map((offset) =>
          getPrayerTimes({
            lat: location.lat,
            lng: location.lng,
            date: new Date(now + offset * day),
            methodId: effectiveMethod,
            madhab,
            timeZone: location.tz,
          }).times
        )
      );
      localStorage.setItem('moslam_theme_schedule', JSON.stringify(schedule));
    } catch {
      // بلا جدول يعود الإقلاع إلى آخر ثيم رُئي
    }
    saveNativeThemeSchedule(schedule, themeChoice);
  }, [location.lat, location.lng, location.tz, effectiveMethod, madhab, themeChoice]);

  const [siteSettings, setSiteSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('moslam_site_settings');
      return saved ? JSON.parse(saved) : {
        siteTitle: 'إذاعة القرآن الكريم',
        siteSubtitle: 'البث المباشر لإذاعات القرآن الكريم وتلاوات كبار القراء',
        announcementText: 'مرحباً بكم في إذاعة القرآن الكريم - استمع إلى التلاوات العطرة على مدار الساعة',
        showAnnouncement: true,
        showHeroStreamInput: true,
        autoPlayOnLoad: false,
        studioLocked: false,
        majlisOpen: false,
        defaultStationId: 'saudi-quran',
      };
    } catch {
      return {
        siteTitle: 'إذاعة القرآن الكريم',
        siteSubtitle: 'البث المباشر لإذاعات القرآن الكريم وتلاوات كبار القراء',
        announcementText: 'مرحباً بكم في إذاعة القرآن الكريم',
        showAnnouncement: true,
        showHeroStreamInput: true,
        autoPlayOnLoad: false,
        studioLocked: false,
        majlisOpen: false,
        defaultStationId: 'saudi-quran',
      };
    }
  });

  // Custom Gallery Images added by Admin
  const [customGalleryImages, setCustomGalleryImages] = useState(() => {
    try {
      const saved = localStorage.getItem('moslam_custom_images');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // قائمة الإذاعات.
  //
  // تُدمج مع الافتراضية بدل استبدالها: سابقاً كانت القائمة تُجمَّد في
  // localStorage عند أول تشغيل، فلا يرى المستخدمون القدامى أي إذاعة
  // تُضاف في تحديث لاحق، ولا أي رابط بث مُصحَّح.
  const [stations, setStations] = useState(() =>
    mergeStations(readJSON('moslam_all_stations', null))
  );

  // Favorites
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem('moslam_favorites');
      return saved ? JSON.parse(saved) : ['cairo-quran', 'saudi-quran'];
    } catch {
      return ['cairo-quran', 'saudi-quran'];
    }
  });

  // Sleep Timer
  const [sleepTimerMinutes, setSleepTimerMinutes] = useState(null);
  const [sleepTimeRemaining, setSleepTimeRemaining] = useState(null);
  const sleepTimerRef = useRef(null);

  // Initialize Audio
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'none';
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    const clearStall = () => {
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
    };

    const onPlay = () => {
      // لا تُمسح المهلة هنا: play يصل لحظة طلب التشغيل قبل أن يُحمَّل شيء،
      // فكان يلغي مهلة التعثّر ويُظهر رسالة الخطأ أثناء إعادة المحاولة.
      // الصوت الفعلي يُعلنه playing.
      setIsPlaying(true);
      setIsLoading(false);
      setErrorMsg(null);
    };

    const onPause = () => {
      setIsPlaying(false);
    };

    const onWaiting = () => {
      // waiting يصل أحياناً وعنصر الصوت فارغ؛ عندها لا شيء يُحمَّل
      if (!audio.currentSrc) return;
      setIsLoading(true);
    };

    const onPlaying = () => {
      clearStall();
      setIsPlaying(true);
      setIsLoading(false);
    };

    const onError = () => {
      // محاولة تشغيل جارية (مهلتها قائمة) تعالج خطأها بنفسها وتعيد
      // المحاولة — فلا تُعرض رسالة لخطأ سيُتجاوز بعد لحظة
      if (stallTimerRef.current) return;
      clearStall();
      setIsLoading(false);
      setIsPlaying(false);
      setErrorMsg('تعذر تشغيل هذا الرابط حالياً، يرجى التأكد من صحة الرابط أو عمل الخادم.');
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('waiting', onWaiting);
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('waiting', onWaiting);
      audio.removeEventListener('playing', onPlaying);
      audio.removeEventListener('error', onError);
      clearStall();
      audio.pause();
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, []);

  // Update Volume
  useEffect(() => {
    const value = isMuted ? 0 : volume;
    if (nativeEngine) nativeSetVolume(value).catch(() => {});
    else if (audioRef.current) audioRef.current.volume = value;
    localStorage.setItem('moslam_volume', volume.toString());
  }, [volume, isMuted, nativeEngine]);

  // اختيار الإذاعة الافتراضية وتشغيلها عند الإقلاع.
  //
  // كان defaultStationId و autoPlayOnLoad يُحفظان من لوحة الأدمن ولا يُقرآن
  // في أي مكان من التطبيق — إعدادان بلا أي أثر.
  const didBootstrapRef = useRef(false);
  useEffect(() => {
    if (didBootstrapRef.current || !audioRef.current) return;
    didBootstrapRef.current = true;

    const enabled = stations.filter((s) => s.isEnabled !== false);
    if (enabled.length === 0) return;

    const preferred =
      enabled.find((s) => s.id === siteSettings.defaultStationId) ?? enabled[0];

    if (siteSettings.autoPlayOnLoad) {
      // قد يمنعه المتصفح قبل تفاعل المستخدم؛ playStation يتعامل مع ذلك برسالة واضحة
      playStation(preferred);
    } else {
      // تحميل البيانات الوصفية فقط دون تشغيل، ليظهر المشغّل جاهزاً
      setCurrentStation(preferred);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist State to LocalStorage
  useEffect(() => {
    localStorage.setItem('moslam_all_stations', JSON.stringify(stations));
  }, [stations]);

  /*
    إعدادات الموقع مصدرها الخادم.

    كانت في localStorage وحده، فكان المسؤول يعدّل اسم الموقع أو الإعلان
    أو الثيم فيراه في متصفّحه ولا يصل أحداً — لوحةٌ تتحكم في جهاز مالكها
    لا في الموقع. تُقرأ هنا عند الإقلاع، وتُكتب من اللوحة إلى الخادم.
  */
  useEffect(() => {
    if (!isApiConfigured()) return;
    let cancelled = false;

    const applyRemote = (remote) => {
      if (!remote) return;

      setSiteSettings((prev) => ({
        ...prev,
        ...(remote.siteTitle ? { siteTitle: remote.siteTitle } : {}),
        ...(remote.announcementText !== undefined
          ? { announcementText: remote.announcementText }
          : {}),
        ...(remote.defaultStationId ? { defaultStationId: remote.defaultStationId } : {}),
        ...(remote.siteSubtitle ? { siteSubtitle: remote.siteSubtitle } : {}),
        showAnnouncement: remote.showAnnouncement === '1',
        showHeroStreamInput: remote.showHeroStreamInput === '1',
        autoPlayOnLoad: remote.autoPlayOnLoad === '1',
        studioLocked: remote.studioLocked === '1',
        majlisOpen: remote.majlisOpen === '1',
        // الفارغ مقصود (يُخفي الزرّ)، فيُنسخ كما هو لا يُتجاوَز
        studioWhatsapp: remote.studioWhatsapp ?? '',
        studioPhone: remote.studioPhone ?? '',
        studioEmail: remote.studioEmail ?? '',
        // خادمٌ أقدم لا يعرف المفتاح = ظاهرة
        showMushaf: remote.showMushaf !== '0',
        showMajlis: remote.showMajlis !== '0',
        showFatwa: remote.showFatwa !== '0',
        showStudio: remote.showStudio !== '0',
      }));

      // عندما ينشر المسؤول ثيماً جديداً للموقع، يُطبَّق فوراً على جميع المستخدمين
      if (remote.defaultTheme) {
        const lastKnown = localStorage.getItem('moslam_last_admin_theme');
        if (remote.defaultTheme !== lastKnown) {
          localStorage.setItem('moslam_last_admin_theme', remote.defaultTheme);
          localStorage.setItem('moslam_theme', remote.defaultTheme);
          setThemeChoice(remote.defaultTheme);
        }
      }
    };

    const loadSettings = () => {
      fetchAppSettings()
        .then((remote) => {
          if (!cancelled && remote) applyRemote(remote);
        })
        .catch(() => {});
    };

    loadSettings();

    // مزامنة دورية كل 30 ثانية وعند رجوع المستخدم إلى التطبيق
    const timer = setInterval(loadSettings, 30000);
    const onFocusOrVisible = () => {
      if (document.visibilityState === 'visible') loadSettings();
    };
    document.addEventListener('visibilitychange', onFocusOrVisible);
    window.addEventListener('focus', onFocusOrVisible);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onFocusOrVisible);
      window.removeEventListener('focus', onFocusOrVisible);
    };
  }, []);

  /*
    الإذاعات والمعرض مصدرهما الخادم.

    كانا في متصفّح المسؤول وحده، فكان يضيف إذاعة أو يعطّلها فلا يراها
    أحد غيره. وقائمة فارغة من الخادم تعني أنه لم ينشر بعد، فتبقى
    القائمة المدمجة — لا شاشة فارغة.
  */
  useEffect(() => {
    if (!isApiConfigured()) return;
    let cancelled = false;

    fetchStations()
      .then((remote) => {
        if (cancelled || !Array.isArray(remote) || remote.length === 0) return;
        setStations(remote);
      })
      .catch(() => {});

    fetchGallery()
      .then((images) => {
        if (cancelled || !Array.isArray(images)) return;
        // الفارغة لا تُكتب — كالإذاعات فوقها.
        //
        // قاعدةٌ نُقلت لتوّها إلى ترحيل 003 معرضُها فارغ، فلو كُتب لمحا
        // صور كل مسؤول من جهازه بلا رجعة. وأولُ صورة تصل الخادمَ تجعله
        // المرجع، فيسري الحذف بعدها.
        if (images.length === 0) return;
        setCustomGalleryImages(images);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('moslam_favorites', JSON.stringify(favorites));
  }, [favorites]);

  // داخل التطبيق: الحالة من المشغّل الأصلي. قد يكون التشغيل بدأ من السيارة
  // أو من شاشة القفل، والصفحة لم تطلب شيئاً.
  const stationsRef = useRef(stations);
  stationsRef.current = stations;
  useEffect(() => {
    if (!nativeEngine) return undefined;
    const apply = (s) => {
      if (!s) return;
      // المشغّل الأصلي يتلو المصحف: ليس للإذاعة شيء يعمل
      if (s.recitation) {
        setIsPlaying(false);
        setIsLoading(false);
        setErrorMsg(null);
        return;
      }
      setIsPlaying(Boolean(s.playing));
      setIsLoading(Boolean(s.loading));
      setErrorMsg(s.error || null);
      if (s.stationId) {
        setCurrentStation((prev) =>
          String(prev?.id) === s.stationId
            ? prev
            : stationsRef.current.find((x) => String(x.id) === s.stationId) ?? prev
        );
      }
    };
    nativeGetState().then(apply).catch(() => {});
    return onNativeState(apply);
  }, [nativeEngine]);

  // شجرة السيارة تُبنى من القائمة الظاهرة والمفضلة، فتُدفع كلما تغيّرتا
  useEffect(() => {
    if (nativeEngine) nativeSetStations(stations, favorites).catch(() => {});
  }, [nativeEngine, stations, favorites]);

  /*
    ما يُقرأ عند الإقلاع يُكتب هنا.

    المفتاحان يُقرآن في useState ويُمحيان عند الإرجاع للمصنع، ولم يكن
    شيء يكتبهما — فتعديل المسؤول يضيع بإعادة التحميل، واللوحةُ تَعِده
    بأنه «يبقى على جهازك حتى يُنشر». وهما كذلك مخزنٌ مؤقّت يُرى قبل أن
    يردّ الخادم، فلا تُرسم اللوحة فارغة ثم تمتلئ.
  */
  useEffect(() => {
    try {
      localStorage.setItem('moslam_site_settings', JSON.stringify(siteSettings));
    } catch {
      // ممتلئ أو محظور: الضياع عند إعادة التحميل أهون من سقوط اللوحة
    }
  }, [siteSettings]);

  useEffect(() => {
    try {
      localStorage.setItem('moslam_custom_images', JSON.stringify(customGalleryImages));
    } catch {
      // كسابقه
    }
  }, [customGalleryImages]);

  // Sleep Timer Handler
  useEffect(() => {
    if (sleepTimerMinutes === null) {
      setSleepTimeRemaining(null);
      if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);
      return;
    }

    const totalSeconds = sleepTimerMinutes * 60;
    setSleepTimeRemaining(totalSeconds);

    if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);

    const startTime = Date.now();
    const endTime = startTime + totalSeconds * 1000;

    sleepTimerRef.current = setInterval(() => {
      const diff = Math.round((endTime - Date.now()) / 1000);
      if (diff <= 0) {
        if (nativeEngine) nativePause().catch(() => {});
        else if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
        setSleepTimerMinutes(null);
        setSleepTimeRemaining(null);
        clearInterval(sleepTimerRef.current);
      } else {
        setSleepTimeRemaining(diff);
      }
    }, 1000);

    return () => {
      if (sleepTimerRef.current) clearInterval(sleepTimerRef.current);
    };
  }, [sleepTimerMinutes]);

  // Play a Station or Stream
  const playStation = (station) => {
    if (!station || !station.url) return;
    setErrorMsg(null);
    setIsLoading(true);
    setCurrentStation(station);

    if (nativeEngine) {
      // المحاولات والاحتياطي ومهلة التعثّر في RadioService؛ الصفحة تنتظر حالته
      nativePlay(station).catch((err) => {
        setIsLoading(false);
        setErrorMsg(err?.message || 'تعذر تشغيل الإذاعة.');
      });
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    // تفريغ أي مصدر سابق بالكامل قبل البدء.
    // سابقاً كان audio.src القديم يبقى عند الانتقال من بث عادي إلى HLS،
    // فيتعارض المصدران ويبقى المشغّل عالقاً أحياناً.
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    audio.pause();
    audio.removeAttribute('src');
    audio.load();

    // علامة لإلغاء المحاولات المتأخرة إن بدّل المستخدم الإذاعة بسرعة
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }

    const token = ++playTokenRef.current;
    const isStale = () => token !== playTokenRef.current;

    const clearStall = () => {
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
    };

    const failWith = (message) => {
      clearStall();
      if (isStale()) return;
      setErrorMsg(message);
      setIsLoading(false);
    };

    const tryStream = async (url, isBackupAttempt = false, attempt = 0) => {
      if (isStale()) return;

      const nextFallback = () => {
        // خادم mp3quran يردّ 500 عشوائياً ثم ينجح الطلب نفسه بعد لحظة
        // (ثلاث مرات متتالية أحياناً)، فكانت أغلب الإذاعات تبدو معطّلة.
        // فيُعاد الرابط نفسه بمهل متزايدة قبل الانتقال إلى الاحتياطي.
        if (attempt < RETRY_DELAYS_MS.length) {
          clearStall();
          stallTimerRef.current = setTimeout(() => {
            stallTimerRef.current = null;
            if (isStale()) return;
            if (hlsRef.current) {
              hlsRef.current.destroy();
              hlsRef.current = null;
            }
            tryStream(url, isBackupAttempt, attempt + 1);
          }, RETRY_DELAYS_MS[attempt]);
          return;
        }
        if (station.backupUrl && !isBackupAttempt && url !== station.backupUrl) {
          tryStream(station.backupUrl, true);
        } else {
          failWith('تعذر تشغيل هذه الإذاعة حالياً. قد يكون الخادم متوقفاً مؤقتاً.');
        }
      };

      // مهلة: خادم يقبل الاتصال ثم يصمت لا يُطلق خطأً أبداً.
      // ١٥ ثانية تكفي أبطأ شبكة، وما زاد فهو تعثّر لا بطء.
      clearStall();
      stallTimerRef.current = setTimeout(() => {
        if (isStale()) return;
        stallTimerRef.current = null;
        nextFallback();
      }, 15000);

      let failed = false;
      const failOnce = () => {
        if (failed || isStale()) return;
        failed = true;
        nextFallback();
      };

      const playDirect = () => {
        // play() لا يرفض دائماً عند فشل المصدر؛ الحدث error هو الأكيد
        audio.addEventListener('error', failOnce, { once: true });
        audio.addEventListener('playing', () => audio.removeEventListener('error', failOnce), {
          once: true,
        });
        audio.src = url;
        audio.play().catch((err) => {
          if (isStale()) return;
          // حظر التشغيل التلقائي ليس عطلاً في الرابط
          if (err?.name === 'NotAllowedError') {
            setIsLoading(false);
            setErrorMsg('اضغط زر التشغيل لبدء البث.');
            return;
          }
          failOnce();
        });
      };

      // Safari يشغّل HLS أصلياً بلا حاجة للمكتبة
      const isHls = url.includes('.m3u8');
      const nativeHls = audio.canPlayType('application/vnd.apple.mpegurl');

      if (!isHls || nativeHls) return playDirect();

      // hls.js ثقيلة (~500KB) وأغلب الإذاعات MP3 عادية،
      // فنجلبها عند أول رابط m3u8 فقط بدل تحميلها مع التطبيق.
      let Hls;
      try {
        ({ default: Hls } = await import('hls.js'));
      } catch {
        return failWith('تعذر تحميل مشغّل البث. تحقق من اتصالك.');
      }

      if (isStale()) return;
      if (!Hls.isSupported()) return playDirect();

      const hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(url);
      hls.attachMedia(audio);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (isStale()) return;
        audio.play().catch(() => failWith('اضغط زر التشغيل لبدء البث.'));
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal || isStale()) return;
        hls.destroy();
        if (hlsRef.current === hls) hlsRef.current = null;
        nextFallback();
      });
      hlsRef.current = hls;
    };

    // streamUrl لم يكن معرَّفاً قط: كل استدعاء لـ playStation كان يرمي
    // ReferenceError قبل أن يلمس عنصر الصوت، فيبقى مؤشّر التحميل يدور بلا
    // بث ولا رسالة خطأ. هذا سبب "الإذاعة لا تعمل" مهما صحّ الرابط.
    tryStream(station.url);

    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: station.name || siteSettings.siteTitle,
        artist: station.reciter || 'بث مباشر',
        album: siteSettings.siteTitle || 'إذاعة القرآن الكريم',
        artwork: [
          {
            src: station.image || DEFAULT_PLACEHOLDER_IMAGE,
            sizes: '512x512',
            type: 'image/jpeg',
          },
        ],
      });

      navigator.mediaSession.setActionHandler('play', () => audio.play());
      navigator.mediaSession.setActionHandler('pause', () => audio.pause());
      navigator.mediaSession.setActionHandler('stop', () => audio.pause());

      // التالي/السابق كانا مفقودين رغم وجود الدالتين — فلم يعملا في شاشة القفل
      const safe = (fn) => {
        try {
          navigator.mediaSession.setActionHandler(fn.action, fn.handler);
        } catch {
          // بعض المتصفحات لا تدعم كل الإجراءات
        }
      };
      safe({ action: 'nexttrack', handler: () => nextStationRef.current?.() });
      safe({ action: 'previoustrack', handler: () => prevStationRef.current?.() });
    }
  };

  const clearError = () => setErrorMsg(null);

  // Play Direct Custom URL
  const playDirectUrl = (url, name = 'بث مباشر خاص', reciter = 'رابط مخصص', image = DEFAULT_PLACEHOLDER_IMAGE) => {
    if (!url || !url.trim()) return;
    const customObj = {
      id: 'custom-' + Date.now(),
      name: name.trim() || 'بث مباشر خاص',
      reciter: reciter.trim() || 'رابط مدخل',
      url: url.trim(),
      image: image || DEFAULT_PLACEHOLDER_IMAGE,
      category: 'إذاعاتي المخصصة',
      isCustom: true,
      isEnabled: true
    };
    playStation(customObj);
  };

  // Toggle Play / Pause
  const togglePlay = () => {
    if (nativeEngine) {
      if (isPlaying) nativePause().catch(() => {});
      else if (currentStation) nativeResume().catch(() => playStation(currentStation));
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      return;
    }
    if (!currentStation) return;

    // عنصر الصوت بلا مصدر: play() هنا لا تُرفض ولا تُحلّ أبداً في WebView
    // أندرويد — تُطلق waiting ثم تصمت. وكان الاعتماد على رفضها هو سبب
    // المؤشّر الدوّار الذي لا ينتهي عند أول ضغطة تشغيل بعد الإقلاع.
    if (!audio.currentSrc) {
      playStation(currentStation);
      return;
    }

    audio.play().catch(() => playStation(currentStation));
  };

  const pauseAudio = () => {
    if (nativeEngine) nativePause().catch(() => {});
    else if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
  };

  const stopAudio = () => {
    if (nativeEngine) {
      nativeStop().catch(() => {});
    } else if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
  };

  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  // Admin: Add Station
  const addStation = (stationData) => {
    const newStation = {
      ...stationData,
      id: stationData.id || 'station-' + Date.now(),
      isCustom: true,
      isEnabled: stationData.isEnabled !== false,
      image: stationData.image || DEFAULT_PLACEHOLDER_IMAGE,
      category: stationData.category || 'إذاعات عامة',
    };
    setStations(prev => [newStation, ...prev]);
    return newStation;
  };

  // Admin: Update Station
  const updateStation = (id, updatedData) => {
    setStations(prev =>
      // isEdited يخبر الدمج عند التحديث بأن يحترم تعديل المستخدم
      // ولا يستبدله بقيم الإذاعة الافتراضية
      prev.map(s => (s.id === id ? { ...s, ...updatedData, isEdited: true } : s))
    );
    if (currentStation && currentStation.id === id) {
      setCurrentStation(prev => ({ ...prev, ...updatedData }));
    }
  };

  // Admin: Delete Station
  const deleteStation = (id) => {
    setStations(prev => prev.filter(s => s.id !== id));
    if (currentStation && currentStation.id === id) {
      if (nativeEngine) nativeStop().catch(() => {});
      else if (audioRef.current) audioRef.current.pause();
      setCurrentStation(null);
    }
  };

  // Admin: Toggle Station Visibility
  const toggleStationEnabled = (id) => {
    setStations(prev =>
      prev.map(s => (s.id === id ? { ...s, isEnabled: s.isEnabled === false ? true : false } : s))
    );
  };

  // Admin: Reset to factory defaults
  const resetToFactoryDefaults = () => {
    const defaultList = DEFAULT_RADIOS.map(r => ({ ...r, isEnabled: true }));
    setStations(defaultList);
    setSiteSettings({
      siteTitle: 'إذاعة القرآن الكريم',
      siteSubtitle: 'البث المباشر لإذاعات القرآن الكريم وتلاوات كبار القراء',
      announcementText: 'مرحباً بكم في إذاعة القرآن الكريم',
      showAnnouncement: true,
      showHeroStreamInput: true,
      autoPlayOnLoad: false,
      defaultStationId: 'saudi-quran',
    });
    setCustomGalleryImages([]);
    setFavorites(['cairo-quran', 'saudi-quran']);
    localStorage.removeItem('moslam_all_stations');
    localStorage.removeItem('moslam_site_settings');
    localStorage.removeItem('moslam_custom_images');
  };

  // Admin: Add custom gallery image
  /*
    المعرض يُكتب على الخادم مباشرة لا محلياً.

    الصورة محتوى مشترك: تُضاف مرة فتصير متاحة لكل من يضبط غلافاً. ولو
    بقيت محلية لرآها من أضافها وحده — وهو ما كان يحدث.
  */
  const addCustomGalleryImage = async (imageObj) => {
    const saved = await addGalleryImage({ title: imageObj.title, url: imageObj.url });
    if (saved) setCustomGalleryImages((prev) => [saved, ...prev]);
    return saved;
  };

  const removeCustomGalleryImage = async (id) => {
    await deleteGalleryImage(id);
    setCustomGalleryImages((prev) => prev.filter((img) => img.id !== id));
  };

  // Favorites
  const toggleFavorite = (id) => {
    setFavorites(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const isFavorite = (id) => favorites.includes(id);

  // Active / Visible stations for visitors
  const visibleStations = isAdminLoggedIn 
    ? stations 
    : stations.filter(s => s.isEnabled !== false);

  const customStations = stations.filter(s => s.isCustom === true);

  // Next / Prev Station
  // مراجع مُحدَّثة تستعملها معالِجات MediaSession، لأنها تُسجَّل مرة واحدة
  // عند بدء التشغيل بينما الدالتان تُعاد إنشاؤهما في كل رندر.
  const nextStationRef = useRef(null);
  const prevStationRef = useRef(null);

  const playNextStation = () => {
    if (!currentStation || visibleStations.length === 0) return;
    const currentIndex = visibleStations.findIndex(s => s.id === currentStation.id);
    const nextIndex = (currentIndex + 1) % visibleStations.length;
    playStation(visibleStations[nextIndex]);
  };

  const playPrevStation = () => {
    if (!currentStation || visibleStations.length === 0) return;
    const currentIndex = visibleStations.findIndex(s => s.id === currentStation.id);
    const prevIndex = (currentIndex - 1 + visibleStations.length) % visibleStations.length;
    playStation(visibleStations[prevIndex]);
  };

  // تحديث المراجع بعد الرندر لا أثناءه
  useEffect(() => {
    nextStationRef.current = playNextStation;
    prevStationRef.current = playPrevStation;
  });

  return (
    <AudioContext.Provider
      value={{
        currentStation,
        isPlaying,
        isLoading,
        volume,
        setVolume,
        isMuted,
        toggleMute,
        errorMsg,
        clearError,
        playStation,
        playDirectUrl,
        togglePlay,
        pauseAudio,
        stopAudio,
        playNextStation,
        playPrevStation,
        stations,
        visibleStations,
        allStations: visibleStations,
        customStations,
        addStation,
        addCustomStation: addStation,
        updateStation,
        deleteStation,
        removeCustomStation: deleteStation,
        toggleStationEnabled,
        resetToFactoryDefaults,
        siteSettings,
        setSiteSettings,
        publishSiteSettings,
        publishStationList,
        isAdminLoggedIn,
        userPermissions,
        canAnswerCalls,
        canOpenRoom,
        canManageRoom,
        isBroadcaster,
        isStaff,
        currentUser,
        isSessionLoading,
        refreshSession,
        setCurrentUser,
        customGalleryImages,
        addCustomGalleryImage,
        removeCustomGalleryImage,
        allGalleryImages: [...customGalleryImages, ...ISLAMIC_IMAGES_GALLERY],
        favorites,
        toggleFavorite,
        isFavorite,
        location,
        gpsLocation,
        selectedCityId,
        isLocating,
        locationError,
        locateMe,
        selectCity,
        calcMethodId,
        setCalcMethodId,
        madhab,
        setMadhab,
        effectiveMethod,
        adhanUrl,
        athkarNotify,
        setAthkarNotify,
        athkarDelay,
        setAthkarDelay,
        setAdhanUrl,
        adhanPermission: adhan.permission,
        adhanSupported: adhan.supported,
        openNotificationSettings: adhan.openSettings,
        THEMES,
        themeChoice,
        activeThemeId,
        activeTheme,
        changeTheme,
        setPrayerPeriod,
        sleepTimerMinutes,
        setSleepTimerMinutes,
        sleepTimeRemaining,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};
